import React from 'react';
import { Table, Input, Button, Space, Typography, Tag, Alert, Tooltip } from 'antd';
import { ReloadOutlined, SearchOutlined, CopyOutlined } from '@ant-design/icons';
import { api } from '../api/client';

export default function Bindings() {
  const [search, setSearch] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const debounceRef = React.useRef(null);

  const fmtTs = React.useCallback((ts) => {
    if (!ts) return '—';
    try {
      return new Date(ts * 1000).toLocaleString('ru-RU');
    } catch (_) {
      return String(ts);
    }
  }, []);

  const copy = React.useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(String(text));
    } catch (_) {
      // ignore
    }
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.getBindings(search);
      setRows(res?.bindings || []);
    } catch (e) {
      setError(e?.message || 'Ошибка загрузки привязок');
    } finally {
      setLoading(false);
    }
  }, [search]);

  const columns = React.useMemo(() => {
    return [
      {
        title: 'chat_id',
        dataIndex: 'client_chat_id',
        key: 'client_chat_id',
        width: 220,
        render: (v) => (
          <Space size={6}>
            <Typography.Text code style={{ fontSize: 12 }}>{String(v)}</Typography.Text>
            <Tooltip title="Скопировать chat_id">
              <Button
                size="small"
                type="text"
                icon={<CopyOutlined />}
                onClick={() => copy(v)}
              />
            </Tooltip>
          </Space>
        )
      },
      {
        title: 'Название',
        dataIndex: 'client_chat_title',
        key: 'client_chat_title',
        ellipsis: true,
        render: (v) => <Typography.Text>{v || '—'}</Typography.Text>
      },
      {
        title: 'Тип',
        dataIndex: 'client_chat_type',
        key: 'client_chat_type',
        width: 140,
        render: (v) => {
          const val = v || '—';
          const color = val === 'private' ? 'blue' : val === 'supergroup' ? 'purple' : val === 'group' ? 'geekblue' : 'default';
          return <Tag color={color}>{String(val)}</Tag>;
        }
      },
      {
        title: '⭐',
        dataIndex: 'star_level',
        key: 'star_level',
        width: 90,
        align: 'center',
        render: (v) => <Tag color="gold">⭐ {v}</Tag>
      },
      {
        title: 'Обновлено',
        dataIndex: 'updated_ts',
        key: 'updated_ts',
        width: 190,
        render: (v) => <Typography.Text>{fmtTs(v)}</Typography.Text>
      }
    ];
  }, [copy, fmtTs]);

  React.useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      load();
    }, 500);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [search, load]);

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>🔗 Привязки клиентских чатов</Typography.Title>
          <Typography.Text type="secondary">Поиск по chat_id или названию. Автопоиск с задержкой 0.5с.</Typography.Text>
        </div>

        <Space.Compact style={{ width: '100%' }}>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по chat_id или названию"
            allowClear
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={load}
            loading={loading}
          >
            Найти
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => load()}
            disabled={loading}
          >
            Обновить
          </Button>
        </Space.Compact>

        {error ? <Alert type="error" showIcon message={error} /> : null}

        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <Typography.Text type="secondary">Найдено: <b>{rows.length}</b></Typography.Text>
        </Space>

        <Table
          rowKey={(r) => r.client_chat_id}
          columns={columns}
          dataSource={rows}
          loading={loading}
          size="middle"
          pagination={{
            pageSize: 25,
            showSizeChanger: true,
            pageSizeOptions: [10, 25, 50, 100],
            showTotal: (total) => `Всего: ${total}`
          }}
          scroll={{ x: 900 }}
          locale={{ emptyText: 'Нет данных' }}
        />
      </Space>
    </div>
  );
}